import { z } from "zod"
import type { AnyObject, DeepPartial } from "./types/custom_type.types"
import { isEmpty } from "./is_empty"
import type { Result } from "./custom_type_guard"
import { Result_ } from "./custom_type_guard"
import {
  type ZodValidatorSchemaRules,
  type ZodValidatorParsed,
  type ZodValidatorShape,
} from "./types/zod.types"

// credits to: https://stackoverflow.com/questions/57593022/reverse-required-and-optional-properties
type OptionalKeys<T> = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  [K in keyof T]-?: {} extends Pick<T, K> ? K : never
}[keyof T]

type FlipOptional<T> = Required<Pick<T, OptionalKeys<T>>> &
  Partial<Omit<T, OptionalKeys<T>>> extends infer O
  ? { [K in keyof O]: O[K] }
  : never

export type Rules<I> = FlipOptional<{
  [K in keyof I]: boolean
}>

export type ExpectedResult<I, R extends Rules<I>> = {
  [K in keyof Required<Rules<I> | I>]: R extends Record<string, never>
    ? NonNullable<I[K]>
    : R[K] extends false
    ? DeepPartial<I[K]> | undefined
    : I[K] extends AnyObject
    ? NonNullable<DeepPartial<I[K]>>
    : NonNullable<I[K]>
}

export type RulesInverted<I> = Partial<{
  [K in keyof I]: boolean
}>
export type ExpectedResultInverted<I, R extends RulesInverted<I>> = {
  [K in keyof Required<RulesInverted<I> | I>]: R extends Record<string, never>
    ? I[K]
    : R[K] extends true
    ? I[K] extends AnyObject
      ? NonNullable<DeepPartial<I[K]>>
      : NonNullable<I[K]>
    : DeepPartial<I[K]> | undefined
}

export const modelValidator = <
  I extends AnyObject = AnyObject,
  R extends Rules<I> = Rules<I>
>(
    input: I,
    rules = {} as R,
  ): Result<ExpectedResult<I, R>> => {
  if (isEmpty(input)) return Result_.err(`modelValidator: input is empty!`)

  for (const key of Object.keys({ ...input, ...rules })) {
    if (
      (isEmpty(rules) || isEmpty(rules[key]) ? true : rules[key]) &&
      isEmpty(input[key])
    )
      return Result_.err(
        `modelValidator: validation failed! ${String(key)} is missing!`,
      )
  }

  return Result_.ok(input as ExpectedResult<I, R>)
}

export const modelValidatorTruths = <
  I extends AnyObject = AnyObject,
  R extends Rules<I> = Rules<I>
>(
    notSure: unknown,
    model: new (...arguments_: any[]) => I,
    rules = {} as R,
  ): notSure is I => {
  const defaultRules: any = {}
  for (const key of Object.keys(new model())) defaultRules[key] = true

  const result = modelValidator<I, R>(notSure as any, {
    /** ...defaultRules, !NOTE: DONT USE THIS FOR NOW, BECAUSE IT WILL BREAK ON RUNTIME */
    ...rules,
  })
  return result.isOk
}

export const modelValidatorInverted = <
  I extends AnyObject = AnyObject,
  R extends RulesInverted<I> = RulesInverted<I>
>(
    input: I,
    rules = {} as R,
  ): Result<ExpectedResultInverted<I, R>> => {
  if (isEmpty(input))
    return Result_.err(`modelValidatorInverted: input is empty!`)

  for (const key of Object.keys({ ...input, ...rules })) {
    if (!isEmpty(rules) && !isEmpty(rules[key]) && isEmpty(input[key]))
      return Result_.err(
        `modelValidatorInverted: validation failed! ${String(key)} is missing!`,
      )
  }

  return Result_.ok(input as ExpectedResultInverted<I, R>)
}

// reference credits: https://github.com/colinhacks/zod/issues/372

export const zodModelValidator = <InputModel extends AnyObject = never>(
  model?: (new (...arguments_: any[]) => InputModel) | InputModel,
) => ({
    rules: <
    SchemaRules extends ZodValidatorShape<InputModel> = ZodValidatorShape<InputModel>
  >(
      schemaRules: SchemaRules,
    ) => ({
      validate(input: unknown) {
        try {
          return Result_.ok(
          z.object(schemaRules).parse(input) as ZodValidatorParsed<SchemaRules>,
          )
        } catch (error) {
          if (error instanceof z.ZodError) {
            return Result_.err({
              public_message: `invalid request input`,
              cause: error,
            })
          }

          return Result_.err({
            cause: error,
            public_message: `model validator unknown error`,
          })
        }
      },
      schemaRules: z.object(schemaRules) as ZodValidatorSchemaRules<SchemaRules>,
      model,
    }),
  })

export { z, z as zod } from "zod"
